import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getContent, getContentList, saveContent, type ContentEntity } from '../../lib/api/content'

type El = Record<string, unknown>

/**
 * Load + save a `single:true` configuration model. The legacy admin treats these as a one-row
 * content model: list it, edit row 0 (create it if missing). We mirror that — load the full
 * entity via getContent() so a save round-trips every untouched field.
 */
export function useSingleConfig(model: string) {
  const qc = useQueryClient()
  const key = ['singleConfig', model]

  const query = useQuery<ContentEntity | null>({
    queryKey: key,
    queryFn: async () => {
      const list = await getContentList(model, { limit: 1 })
      const first = list.data[0]
      if (!first) return null
      return getContent(model, first.id)
    },
  })

  const save = useMutation({
    // `patch` is shallow-merged onto the loaded elements, so callers pass only what changed
    // (nested integration objects are passed whole).
    mutationFn: async (patch: El) => {
      const base = query.data
      const entity: ContentEntity = base
        ? { ...base, elements: { ...(base.elements as El), ...patch } }
        : { id: '', modelId: model, elements: patch }
      return saveContent(model, entity)
    },
    onSuccess: (saved) => {
      qc.setQueryData(key, saved)
    },
  })

  return { query, save }
}

/**
 * Form-state wrapper around a single-config model: seeds the form from the loaded entity,
 * tracks dirtiness against a snapshot, and persists via the shallow-merge save. `seed` maps
 * stored elements → form values; `commit` maps form values → the element patch to save.
 */
export function useConfigForm<T>(model: string, seed: (el: El) => T) {
  const { query, save } = useSingleConfig(model)
  const seedRef = useRef(seed)
  seedRef.current = seed

  const [form, setForm] = useState<T | null>(null)
  const [snapshot, setSnapshot] = useState('')
  const [ok, setOk] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // (Re)seed whenever the loaded entity changes — including right after a successful save.
  useEffect(() => {
    if (!query.isSuccess) return
    const f = seedRef.current((query.data?.elements ?? {}) as El)
    setForm(f)
    setSnapshot(JSON.stringify(f))
  }, [query.isSuccess, query.data])

  const dirty = form != null && JSON.stringify(form) !== snapshot

  const patch = (p: Partial<T>) => {
    setForm((f) => (f ? { ...f, ...p } : f))
    setOk(false)
  }

  const reset = () => {
    if (!query.data && query.data !== null) return
    const f = seedRef.current((query.data?.elements ?? {}) as El)
    setForm(f)
    setSnapshot(JSON.stringify(f))
    setOk(false)
    setError(null)
  }

  async function commit(toPatch: (f: T) => El) {
    if (!form) return
    setError(null)
    try {
      await save.mutateAsync(toPatch(form))
      setSnapshot(JSON.stringify(form))
      setOk(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Salvataggio non riuscito.')
    }
  }

  return { query, form, dirty, patch, reset, commit, saving: save.isPending, ok, error }
}
